import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Lock, Undo2, Banknote, Loader2, Clock } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const ST = { held: "bg-blue-500/20 text-blue-300", expired_pending: "bg-amber-500/20 text-amber-300", refunded: "bg-emerald-500/20 text-emerald-300", kept: "bg-slate-700 text-slate-200" };

export default function DepositQueue({ onChanged }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("open");
  const [note, setNote] = useState({});
  const [busy, setBusy] = useState("");

  const load = async () => {
    const { data } = await api.get("/admin/reservations");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const resolve = async (id, action) => {
    setBusy(id + action);
    try {
      await api.post(`/admin/reservations/${id}/resolve`, { action, note: note[id] || null });
      toast.success(action === "refund" ? "Deposit refunded via Stripe" : "Deposit kept");
      await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); } finally { setBusy(""); }
  };

  const shown = items.filter((r) => filter === "all" || (filter === "open" ? ["held", "expired_pending"].includes(r.status) : r.status === filter));
  const hoursLeft = (r) => Math.max(0, Math.round((new Date(r.expires_at) - Date.now()) / 36e5));

  return (
    <div data-testid="deposits-tab">
      <div className="flex gap-2 mb-4 flex-wrap">
        {["open", "held", "expired_pending", "refunded", "kept", "all"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`deposit-filter-${f}`} className={`px-3 py-1.5 rounded-full text-xs font-mono uppercase border ${filter === f ? "border-cyan-500/50 bg-cyan-500/10 text-cyan-300" : "border-slate-800 text-slate-400 hover:border-slate-600"}`}>{f.replace("_", " ")}</button>
        ))}
      </div>
      {shown.length === 0 && <p className="text-sm text-slate-500 p-8 text-center border border-dashed border-slate-800 rounded-xl" data-testid="deposits-empty">No deposits in this view.</p>}
      <div className="space-y-3">
        {shown.map((r) => {
          const open = ["held", "expired_pending"].includes(r.status);
          return (
            <div key={r.reservation_id} className="p-4 rounded-2xl bg-[#131722] border border-slate-800" data-testid={`deposit-${r.reservation_id}`}>
              <div className="flex items-center gap-3 flex-wrap">
                <Lock className="w-4 h-4 text-amber-400" />
                <Link to={`/buy/${r.listing_id}`} className="font-semibold hover:text-amber-300">{r.listing_label}</Link>
                <span className="font-mono text-emerald-300">${Number(r.amount).toFixed(2)}</span>
                <Badge className={`${ST[r.status]} border-0 text-[10px] uppercase`}>{r.status.replace("_", " ")}</Badge>
                {r.status === "held" && <span className="text-xs text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" />{hoursLeft(r)}h left</span>}
                <span className="ml-auto text-[11px] font-mono text-slate-500">{new Date(r.created_at).toLocaleString()}</span>
              </div>
              {open ? (
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-2">
                  <Input value={note[r.reservation_id] || ""} onChange={(e) => setNote((n) => ({ ...n, [r.reservation_id]: e.target.value }))} placeholder="Note to buyer & seller (optional)" data-testid={`deposit-note-${r.reservation_id}`} className="bg-slate-900 border-slate-800" />
                  <Button size="sm" disabled={!!busy} onClick={() => resolve(r.reservation_id, "refund")} data-testid={`deposit-refund-${r.reservation_id}`} className="bg-emerald-500 text-slate-950 hover:bg-emerald-400 font-semibold">
                    {busy === r.reservation_id + "refund" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Undo2 className="w-3.5 h-3.5 mr-1.5" />Refund deposit</>}
                  </Button>
                  <Button size="sm" variant="outline" disabled={!!busy} onClick={() => resolve(r.reservation_id, "keep")} data-testid={`deposit-keep-${r.reservation_id}`} className="bg-transparent border-slate-700 text-amber-300">
                    {busy === r.reservation_id + "keep" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Banknote className="w-3.5 h-3.5 mr-1.5" />Keep deposit</>}
                  </Button>
                </div>
              ) : (
                <p className="text-xs text-slate-400 mt-2">Resolved {r.resolved_at && new Date(r.resolved_at).toLocaleString()}{r.note && ` · "${r.note}"`}{r.stripe_refund_id && <span className="font-mono text-slate-500"> · {r.stripe_refund_id}</span>}</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
